import dayjs from "dayjs";
import { hasPms } from "../utils/partnerPms";
import { apiCollectionToRowResponse, rowQueryToApiParams } from "../modules/tableQuery";
import { AdminContainer } from "../components/layouts";
import MaterialTable from "material-table";
import React, { useState, useEffect, useRef } from "react";
import Time from "../lithic/Moment";
import api from "../api";
import tableIcons from "../components/tableIcons";
import { useTheme } from "@material-ui/core/styles";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import { startCase } from "lodash";
import { formatMoney } from "../lithic/Money";
import { debounce } from "lodash";
import { Button, LinearProgress, makeStyles, MenuItem, Select } from "@material-ui/core";
import { toast } from "react-toastify";
import ConfirmationDialog from "../components/ConfirmationDialog";
import useToggle from "../state/useToggle";
import DatePicker from "../components/DatePicker";
import { saveAs } from "file-saver";
import fetchPagesInParallel from "../utils/fetchPagesInParallel";

const useStyles = makeStyles({
  detailPanel: {
    padding: 20,
  },
  sectionWrapper: {
    marginTop: "20px",
  },
  codeBlock: {
    whiteSpace: "pre-wrap",
    maxHeight: 400,
    overflow: "auto",
    padding: "10px",
    backgroundColor: "#f5f5f5",
    borderRadius: "4px",
  },
  smallCodeBlock: {
    whiteSpace: "pre-wrap",
    maxHeight: 200,
    overflow: "auto",
    padding: "10px",
    backgroundColor: "#f5f5f5",
    borderRadius: "4px",
  },
});

export default function PmsChargesListPage() {
  const { id: partnerId } = useParams();

  const lastDataRequestQueryRef = useRef({});
  const tableRef = useRef();
  const theme = useTheme();
  const classes = useStyles();

  const [partner, setPartner] = useState({});
  const [selectedCharge, setSelectedCharge] = useState(null);
  const [, setActionType] = useState(null);
  const [downloadProgress, setDownloadProgress] = useState(null);

  const rerunToggle = useToggle();
  const reverseToggle = useToggle();

  const handleRerunClick = (id) => {
    setSelectedCharge(id);
    setActionType("rerun");
    rerunToggle.turnOn();
  };

  const handleReverseClick = (id) => {
    setSelectedCharge(id);
    setActionType("reverse");
    reverseToggle.turnOn();
  };

  const handleConfirmRerun = async () => {
    if (!selectedCharge) return;

    await api
      .rerunPmsCharge(partnerId, selectedCharge)
      .then(() => {
        toast.success("Charge rerun initiated successfully");
        tableRef.current?.onQueryChange();
      })
      .catch((error) => {
        toast.error("Failed to rerun charge: " + (error.message || "Unknown error"));
      })
      .finally(() => {
        rerunToggle.turnOff();
        setSelectedCharge(null);
        setActionType(null);
      });
  };

  const handleConfirmReverse = async () => {
    if (!selectedCharge) return;

    await api
      .reversePmsCharge(partnerId, selectedCharge)
      .then(() => {
        toast.success("Charge reverse initiated successfully");
        tableRef.current?.onQueryChange();
      })
      .catch((error) => {
        toast.error("Failed to reverse charge: " + (error.message || "Unknown error"));
      })
      .finally(() => {
        reverseToggle.turnOff();
        setSelectedCharge(null);
        setActionType(null);
      });
  };

  const handleCancelAction = () => {
    rerunToggle.turnOff();
    reverseToggle.turnOff();
    setSelectedCharge(null);
    setActionType(null);
  };

  const handleDownloadCsv = async () => {
    setDownloadProgress({ loaded: 0, total: 0 });
    try {
      const currentQuery = lastDataRequestQueryRef.current;
      let params = rowQueryToApiParams(currentQuery, true);

      const searchHasOnlyDigits = /^\d+$/.test(params.search);

      const filterParams = (params.filters || []).reduce((acc, filter) => {
        if (filter.values) {
          acc[filter.name] = filter.values;
        }
        return acc;
      }, {});

      const chargedDateFilter = filterParams.date;
      const createdAtFilter = filterParams.createdAt;

      const chargedDateString = chargedDateFilter
        ? dayjs(chargedDateFilter).format("YYYY-MM-DD")
        : null;
      const createdAtString = createdAtFilter
        ? dayjs(createdAtFilter).format("YYYY-MM-DD")
        : null;

      const baseParams = {
        ...params,
        ...filterParams,
        ...(chargedDateString
          ? { from_date: chargedDateString, to_date: chargedDateString }
          : {}),
        ...(createdAtString
          ? {
              from_created_at_date: createdAtString,
              to_created_at_date: createdAtString,
            }
          : {}),
        filter: [],
        search: searchHasOnlyDigits ? "" : params.search,
        enrollment_id: searchHasOnlyDigits ? currentQuery.search : null,
      };

      const items = await fetchPagesInParallel({
        requestPage: ({ page, pageSize }) =>
          api.getPmsCharges(partnerId, {
            ...baseParams,
            page: page + 1,
            perPage: pageSize,
          }),
        onProgress: setDownloadProgress,
      });

      const csvContent = convertToCsv(items);

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const fileName = `${partner.name}_pms_charges_${
        new Date().toISOString().split("T")[0]
      }.csv`;

      saveAs(blob, fileName);
      toast.success(`CSV export completed! ${items.length} records exported.`);
    } catch (error) {
      toast.error("Error exporting CSV: " + (error.message || "Unknown error"));
    } finally {
      setDownloadProgress(null);
    }
  };

  const convertToCsv = (data) => {
    if (!data || data.length === 0) return "";

    const headers = [
      "Reference ID",
      "Property Name",
      "Property Address 1",
      "Property Address 2",
      "State",
      "Zip Code",
      "Lease Start Date",
      "Charge Description",
      "Post Date",
      "Charge Type",
      "Amount",
      "Status",
      "Failure Reason",
      "Customer Name",
      "Customer Email",
      "Created At",
    ];

    const rows = data.map((item) => {
      const customerName = [item.customer?.firstName, item.customer?.lastName]
        .filter(Boolean)
        .join(" ");

      const amount = item.amount ? formatMoney(item.amount) : "--";
      const postDate = item.date ? dayjs(item.date).format("MMM DD, YYYY") : "";
      const createdAt = item.createdAt
        ? dayjs(item.createdAt).format("MMM DD, YYYY hh:mm a")
        : "";
      const leaseStartDate = item.leaseStartDate
        ? dayjs(item.leaseStartDate).format("MMM DD, YYYY")
        : "";

      // Extract failure reason from response for failed charges
      let failureReason = "";
      if (item.status === "failed" && item.response) {
        const response =
          typeof item.response === "string" ? JSON.parse(item.response) : item.response;
        failureReason =
          response?.error || response?.message || response?.errors?.[0] || "";
      }

      return [
        item.id || "",
        item.propertyName || "",
        item.propertyAddress1 || "",
        item.propertyAddress2 || "",
        item.propertyState || "",
        item.propertyZip || "",
        leaseStartDate,
        item.chargeDescription || item.kind || "",
        postDate,
        item.kind || "",
        amount,
        item.status || "",
        failureReason,
        customerName,
        item.customer?.email || "",
        createdAt,
      ];
    });

    const escapeCsvValue = (value) => {
      if (value === null || value === undefined) return "";
      const stringValue = String(value);
      if (
        stringValue.includes(",") ||
        stringValue.includes('"') ||
        stringValue.includes("\n")
      ) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    };

    const csvRows = [
      headers.map(escapeCsvValue).join(","),
      ...rows.map((row) => row.map(escapeCsvValue).join(",")),
    ];

    return csvRows.join("\n");
  };

  const columns = makeColumns(theme, partner, handleRerunClick, handleReverseClick);

  const fetchData = debounce((q, resolve) => {
    lastDataRequestQueryRef.current = q;

    let params = rowQueryToApiParams(q, true);

    const searchHasOnlyDigits = /^\d+$/.test(params.search);

    const filterParams = (params.filters || []).reduce((acc, filter) => {
      if (filter.values) {
        acc[filter.name] = filter.values;
      }

      return acc;
    }, {});

    const chargedDateFilter = filterParams.date;
    const createdAtFilter = filterParams.createdAt;

    const chargedDateString = chargedDateFilter
      ? dayjs(chargedDateFilter).format("YYYY-MM-DD")
      : null;
    const createdAtString = createdAtFilter
      ? dayjs(createdAtFilter).format("YYYY-MM-DD")
      : null;

    params = {
      ...params,
      ...filterParams,
      ...(chargedDateString
        ? { from_date: chargedDateString, to_date: chargedDateString }
        : {}),
      ...(createdAtString
        ? { from_created_at_date: createdAtString, to_created_at_date: createdAtString }
        : {}),
      filter: [], // We are not using filters in this format in BE
      search: searchHasOnlyDigits ? "" : params.search,
      enrollment_id: searchHasOnlyDigits ? q.search : null,
    };

    api
      .getPmsCharges(partnerId, params)
      .then((resp) => resolve(apiCollectionToRowResponse(resp)))
      .catch(() => {
        toast.error("Error fetching PMS charges");
      });
  }, 300);

  const formatJson = (data) => {
    if (!data) return "None";
    try {
      if (typeof data === "object") {
        return JSON.stringify(data, null, 2);
      }

      if (typeof data === "string") {
        return JSON.stringify(JSON.parse(data), null, 2);
      }
    } catch (e) {
      return "Error parsing JSON";
    }
  };

  useEffect(() => {
    api.getPartner({ id: partnerId }).then(api.pickData).then(setPartner);
  }, [partnerId]);

  return (
    <AdminContainer variant="list">
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", marginBottom: "16px", gap: "8px" }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleDownloadCsv}
          disabled={downloadProgress !== null}
        >
          Download CSV
        </Button>
        {downloadProgress !== null && (
          <div style={{ width: "240px" }}>
            <LinearProgress
              variant="determinate"
              value={
                downloadProgress.total > 0
                  ? (downloadProgress.loaded / downloadProgress.total) * 100
                  : 0
              }
            />
            <div
              style={{
                fontSize: "0.75rem",
                marginTop: "4px",
                textAlign: "right",
                color: "rgba(0,0,0,0.6)",
              }}
            >
              {downloadProgress.total > 0
                ? `Loaded ${downloadProgress.loaded} of ${downloadProgress.total} pages…`
                : "Preparing download…"}
            </div>
          </div>
        )}
      </div>
      <MaterialTable
        title={`${partner.name || "Partner"}'s PMS Charges`}
        tableRef={tableRef}
        columns={columns}
        options={{
          emptyRowsWhenPaging: false,
          filtering: true,
          sorting: true,
          pageSize: 50,
          pageSizeOptions: [50, 100, 200],
        }}
        data={(q) =>
          new Promise((resolve) => {
            fetchData(q, resolve);
          })
        }
        detailPanel={(rowData) => {
          if (!rowData) return null;

          return (
            <div className={classes.detailPanel}>
              <div className={classes.sectionWrapper}>
                <h3>Request</h3>
                <pre className={classes.smallCodeBlock}>
                  {formatJson(rowData.request)}
                </pre>
              </div>

              <div className={classes.sectionWrapper}>
                <h3>Response</h3>
                <pre className={classes.codeBlock}>{formatJson(rowData.response)}</pre>
              </div>
            </div>
          );
        }}
        icons={tableIcons}
      />

      <ConfirmationDialog
        open={rerunToggle.on}
        onCancel={handleCancelAction}
        onConfirm={handleConfirmRerun}
        title="Confirm Rerun Charge"
        text={`Are you sure you want to rerun charge ${selectedCharge}? This will attempt to process the charge again.`}
        cancelProps={{ children: "Cancel" }}
        confirmProps={{ children: "Rerun Charge" }}
      />

      <ConfirmationDialog
        open={reverseToggle.on}
        onCancel={handleCancelAction}
        onConfirm={handleConfirmReverse}
        title="Confirm Reverse Charge"
        text={`Are you sure you want to reverse charge ${selectedCharge}? This action cannot be undone.`}
        cancelProps={{ children: "Cancel" }}
        confirmProps={{ children: "Reverse Charge" }}
      />
    </AdminContainer>
  );
}

function makeColumns(theme, partner, handleRerunClick, handleReverseClick) {
  return [
    {
      title: "Reference ID",
      field: "id",
      defaultSort: "desc",
      filtering: false,
      render: (rowData) => rowData.id,
    },
    {
      title: "Property Name",
      field: "propertyName",
      filtering: false,
      render: (rowData) => rowData.propertyName || "--",
    },
    {
      title: "Property Address 1",
      field: "propertyAddress1",
      filtering: false,
      render: (rowData) => rowData.propertyAddress1 || "--",
    },
    {
      title: "Charge Description",
      field: "chargeDescription",
      filtering: false,
      render: (rowData) => rowData.chargeDescription || rowData.kind || "--",
    },
    {
      title: "Post Date",
      field: "date",
      render: (rowData) => <Time formatExact="MMM DD, YYYY">{rowData.date}</Time>,
      filterComponent: ({ columnDef, onFilterChanged }) => {
        const filterValue = columnDef.tableData.filterValue;

        return (
          <DatePicker
            value={filterValue}
            onChange={(date) => onFilterChanged(columnDef.tableData.id, date)}
            format="MMM DD, YYYY"
            clearable
            clearLabel="Clear"
            defaultEmptyValue
          />
        );
      },
    },
    {
      title: "Charge Type",
      field: "kind",
      filtering: true,
      render: (rowData) => rowData.kind,
    },
    {
      title: "Amount",
      filtering: false,
      field: "amount.cents",
      render: (rowData) => (rowData.amount ? formatMoney(rowData.amount) : "--"),
    },
    {
      title: "Status",
      field: "status",
      filterComponent: ({ columnDef, onFilterChanged }) => {
        const filterValue = columnDef.tableData.filterValue || "";
        return (
          <Select
            value={filterValue}
            onChange={(e) => onFilterChanged(columnDef.tableData.id, e.target.value)}
            style={{ width: "100%" }}
            displayEmpty
            MenuProps={{
              anchorOrigin: {
                vertical: "bottom",
                horizontal: "left",
              },
              transformOrigin: {
                vertical: "top",
                horizontal: "left",
              },
              getContentAnchorEl: null,
            }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="posted">Posted</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
            <MenuItem value="reversed">Reversed</MenuItem>
          </Select>
        );
      },
      render: (rowData) => (
        <span
          style={{ color: getStatusColor(rowData.status, theme), fontWeight: "bold" }}
        >
          {startCase(rowData.status || "")}
        </span>
      ),
    },
    {
      title: "Name",
      field: "name",
      filtering: false,
      render: (rowData) =>
        [rowData.customer?.firstName, rowData.customer?.lastName]
          .filter(Boolean)
          .join(" "),
    },
    {
      title: "Email",
      field: "email",
      filtering: false,
      render: (rowData) => rowData.customer?.email,
    },
    {
      title: "Created At",
      field: "createdAt",
      filtering: true,
      render: (rowData) => (
        <Time formatExact="MMM DD, YYYY hh:mm a">{rowData.createdAt}</Time>
      ),
      filterComponent: ({ columnDef, onFilterChanged }) => {
        const filterValue = columnDef.tableData.filterValue;

        return (
          <DatePicker
            value={filterValue}
            onChange={(createdAt) => onFilterChanged(columnDef.tableData.id, createdAt)}
            format="MMM DD, YYYY"
            clearable
            clearLabel="Clear"
            defaultEmptyValue
          />
        );
      },
    },
    {
      title: "Actions",
      field: "actions",
      filtering: false,
      render: (rowData) => {
        const { id, status } = rowData;
        const actions = [];

        if (status === "failed" && !hasPms(partner, "yardi")) {
          actions.push(
            <Button
              key="rerun"
              variant="contained"
              color="primary"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleRerunClick(id);
              }}
              style={{
                marginRight: "4px",
                minWidth: "60px",
                fontSize: "0.75rem",
              }}
            >
              Rerun
            </Button>
          );
        }

        if (status === "posted" && hasPms(partner, "entrata")) {
          actions.push(
            <Button
              key="reverse"
              variant="contained"
              color="primary"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleReverseClick(id);
              }}
              style={{
                minWidth: "60px",
                fontSize: "0.75rem",
              }}
            >
              Reverse
            </Button>
          );
        }

        return actions.length > 0 ? (
          <div style={{ display: "flex", gap: "4px", justifyContent: "flex-end" }}>
            {actions}
          </div>
        ) : (
          ""
        );
      },
    },
  ];
}

function getStatusColor(status, theme) {
  const statusColors = {
    pending: theme.palette.warning.main,
    posted: theme.palette.success.main,
    failed: theme.palette.error.main,
    reversed: theme.palette.text.secondary,
  };

  return statusColors[status] || theme.palette.text.secondary;
}
